<script>
  import { onMount, onDestroy } from "svelte";
  import Icon from "@iconify/svelte";
  import { t } from "./lib/i18n";

  import webhookIcon from "@iconify/icons-mdi/webhook";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import closeIcon from "@iconify/icons-mdi/close";
  import openInNewIcon from "@iconify/icons-mdi/open-in-new";
  import magnifyIcon from "@iconify/icons-mdi/magnify";
  import chevronRightIcon from "@iconify/icons-mdi/chevron-right";
  import clockIcon from "@iconify/icons-mdi/clock-outline";
  import checkIcon from "@iconify/icons-mdi/check-circle-outline";
  import alertIcon from "@iconify/icons-mdi/alert-circle-outline";

  export let apiBase = "";
  export let apiKey = "";
  export let authMode = false; // IMPORTANT: passed from App.svelte

  const JSON_HEADERS = { Accept: "application/json" };

  // raw webhook attempts
  let attempts = [];
  let loading = false;
  let err = "";

  // cursor paging
  let limit = 50;
  let cursor = 0;
  let nextCursor = -1;
  let totalJobs = 0;
  let cursorStack = [];

  // client-side filters
  let q = "";
  let eventFilter = "all"; // all | picked | starting | finished | error

  // drawer
  let drawerOpen = false;
  let selectedKey = "";
  let selectedGroup = null;

  // inflight abort + poller
  let inflight = null;
  let poller = null;

  const POLL_MS = 2500;

  function keyTrim() {
    return String(apiKey || "").trim();
  }

  function canUseJSON() {
    return !authMode || !!keyTrim();
  }

  function jsonAuthHeaders() {
    const h = { ...JSON_HEADERS };
    const k = keyTrim();
    if (k) h.Authorization = `Bearer ${k}`;
    return h;
  }

  function toQS(obj) {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(obj)) {
      if (v === undefined || v === null) continue;
      const s = String(v).trim();
      if (!s) continue;
      p.set(k, s);
    }
    const out = p.toString();
    return out ? `?${out}` : "";
  }

  function safeStr(v) {
    const s = (v ?? "").toString().trim();
    return s ? s : "\u2014";
  }

  function ts(v) {
    if (!v) return 0;
    const d = new Date(v);
    const n = d.getTime();
    return Number.isFinite(n) ? n : 0;
  }

  function fmtTime(v) {
    if (!v) return "\u2014";
    const d = new Date(v);
    if (isNaN(d.getTime())) return safeStr(v);
    return d.toLocaleString();
  }

  function fmtMs(ms) {

    const n = Number(ms || 0);
    if (!Number.isFinite(n) || n <= 0) return "\u2014";
    if (n < 1000) return `${n} ms`;
    return `${(n / 1000).toFixed(2)} s`;
  }

  function isDeliveredFromHTTP(code) {
    const n = Number(code || 0);
    return n >= 200 && n < 300;
  }

  function statusOfAttempt(a) {
    if (a?.http_status === undefined || a?.http_status === null) return "unknown";
    return isDeliveredFromHTTP(a.http_status) ? "delivered" : "failed";
  }

  function statusIcon(s) {
    const v = String(s || "").toLowerCase();
    if (v === "delivered") return checkIcon;
    if (v === "failed") return alertIcon;
    return clockIcon;
  }

  function statusPillClass(_) {
    return "border-white/10 bg-white/[0.03] text-zinc-200";
  }

  function httpCodeClass(code) {
    const n = Number(code || 0);
    if (n >= 200 && n < 300) return "text-emerald-200";
    if (n >= 300 && n < 400) return "text-blue-200";
    if (n >= 400 && n < 600) return "text-rose-200";
    return "text-zinc-500";
  }

  function shortUrl(u) {
    const s = (u || "").toString().trim();
    if (!s) return "\u2014";
    try {
      const x = new URL(s);
      const host = x.host;
      const path = x.pathname.replace(/\/+$/, "");
      const tail = path.split("/").filter(Boolean).slice(-2).join("/");
      return `${host}/${tail || ""}`.replace(/\/$/, "");
    } catch {
      const parts = s.split("/").filter(Boolean);
      return parts.length > 2 ? parts.slice(-2).join("/") : s;
    }
  }

  function matchQ(a, query) {
    const qq = String(query || "").trim().toLowerCase();
    if (!qq) return true;
    const hay = [a?.job_id, a?.event_type, a?.url, a?.tenant_id, a?.http_status]
      .map((x) => (x ?? "").toString().toLowerCase())
      .join(" ");
    return hay.includes(qq);
  }

  function groupAttempts(list) {
    const m = new Map();

    for (const a of list || []) {
      const ev = (a?.event_type || "unknown").toString();
      const job = (a?.job_id || a?.id || "unknown").toString();
      const key = `${job}::${ev}`;

      if (!m.has(key)) {
        m.set(key, {
          key,
          job_id: job,
          event_type: ev,
          last_ts: a?.timestamp || "",
          targets: []
        });
      }

      const g = m.get(key);
      if (ts(a?.timestamp) > ts(g.last_ts)) g.last_ts = a?.timestamp || g.last_ts;

      g.targets.push({
        url: a?.url || "",
        http_status: a?.http_status,
        duration_ms: a?.duration_ms,
        attempt: a?.attempt
      });
    }

    const out = Array.from(m.values()).map((g) => {
      const delivered = g.targets.filter((t) => statusOfAttempt(t) === "delivered").length;
      const failed = g.targets.filter((t) => statusOfAttempt(t) === "failed").length;
      const unknown = g.targets.filter((t) => statusOfAttempt(t) === "unknown").length;

      let status = "unknown";
      if (failed > 0) status = "failed";
      else if (delivered > 0 && unknown === 0) status = "delivered";

      return {
        ...g,
        delivered,
        failed,
        unknown,
        total_targets: g.targets.length,
        status
      };
    });

    out.sort((a, b) => {
      const fa = a.status === "failed" ? 1 : 0;
      const fb = b.status === "failed" ? 1 : 0;
      if (fa !== fb) return fb - fa;
      const dt = ts(b.last_ts) - ts(a.last_ts);
      if (dt !== 0) return dt;
      return (a.key || "").localeCompare(b.key || "");
    });

    return out;
  }

  $: filteredAttempts =
    (attempts || [])
      .filter((a) => (eventFilter === "all" ? true : String(a?.event_type || "") === eventFilter))
      .filter((a) => matchQ(a, q));

  $: groups = groupAttempts(filteredAttempts);

  function startPolling() {
    if (poller) return;
    poller = setInterval(() => {
      if (!canUseJSON()) return;
      if (!loading) fetchWebhooks(); // don't stack
    }, POLL_MS);
  }

  function stopPolling() {
    if (!poller) return;
    clearInterval(poller);
    poller = null;
  }

  // Auth gate: if auth required but missing key, stop + cancel.
  $: if (!canUseJSON()) {
    stopPolling();
    try { inflight?.abort?.(); } catch {}
  } else {
    startPolling();
  }

  // Abort ONLY when apiKey actually changes (not every reactive tick)
  let lastKey = keyTrim();
  $: {
    const nowKey = keyTrim();
    if (nowKey !== lastKey) {
      lastKey = nowKey;
      try { inflight?.abort?.(); } catch {}
      // Optional: refresh immediately when key appears/changes
      if (canUseJSON()) fetchWebhooks(0);
    }
  }

  async function fetchWebhooks(next = null) {
    if (!canUseJSON()) return;

    loading = true;
    err = "";
    try {
      const useCursor = next === null ? cursor : next;

      const qs = toQS({ cursor: useCursor, limit });

      try { inflight?.abort?.(); } catch {}
      inflight = new AbortController();

      const res = await fetch(`${apiBase}/webhooks${qs}`, {
        cache: "no-store",
        signal: inflight.signal,
        headers: jsonAuthHeaders()
      });

      let body = null;
      try { body = await res.json(); } catch {}

      if (!res.ok) throw new Error(body?.error || `webhooks failed: ${res.status}`);

      attempts = body?.items || [];
      totalJobs = Number(body?.total_jobs ?? body?.total ?? 0);
      cursor = Number(body?.cursor ?? useCursor ?? 0);
      nextCursor = Number(body?.next_cursor ?? -1);

      // refresh selectedGroup from the NEW attempts (don't rely on old $:groups timing)
      if (drawerOpen && selectedKey) {
        const refreshed = groupAttempts(filteredAttempts).find((g) => g.key === selectedKey);
        if (refreshed) selectedGroup = refreshed;
      }
    } catch (e) {
      if (e?.name === "AbortError") return;
      err = e?.message || String(e);
      attempts = [];
      totalJobs = 0;
      nextCursor = -1;
    } finally {
      loading = false;
    }
  }

  function prev() {
    if (!cursorStack.length) return;
    const prevCursor = cursorStack[cursorStack.length - 1];
    cursorStack = cursorStack.slice(0, -1);
    fetchWebhooks(prevCursor);
  }

  function next() {
    if (nextCursor < 0) return;
    cursorStack = [...cursorStack, cursor];
    fetchWebhooks(nextCursor);
  }

  function openGroup(g) {
    selectedGroup = g;
    selectedKey = g.key;

    drawerOpen = true;
  }

  function closeDrawer() {
    drawerOpen = false;
    selectedKey = "";
    selectedGroup = null;
  }

  onMount(() => {
    // initial load + polling (reactive gate keeps it correct)
    fetchWebhooks();
    startPolling();
  });

  onDestroy(() => {
    stopPolling();
    try { inflight?.abort?.(); } catch {}
  });

  // UI tokens
  const headerChip =
    "rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-zinc-300 inline-flex items-center gap-2";

  const btn =
    "inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-zinc-200 hover:bg-white/[0.06] disabled:opacity-50 disabled:cursor-not-allowed";

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none focus:border-white/20";

  const selectCls =
    "rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-sm text-zinc-200 outline-none focus:border-white/20";

  const rowBase =
    "w-full text-left rounded-xl border transition-colors p-4 relative overflow-hidden";

  const rowIdle = "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]";

  const rowSelected =
    "border-[rgba(74,222,128,0.70)] " +
    "bg-gradient-to-r from-[rgba(74,222,128,0.14)] via-[rgba(74,222,128,0.06)] to-transparent " +
    "ring-1 ring-[rgba(74,222,128,0.50)] " +
    "before:absolute before:top-0 before:bottom-0 before:left-0 before:w-[3px] " +
    "before:bg-[rgb(74,222,128)] before:rounded-none " +
    "before:shadow-[0_0_18px_rgba(74,222,128,0.85)]";

  const card = "rounded-xl border border-white/5 bg-white/[0.02]";

  const pill =
    "inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-semibold";

  const tinyChip =
    "inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-zinc-300";
</script>


<main class="w-full px-4 py-6">
  <div class="flex gap-4">
    <!-- MAIN LIST -->
    <section
      class={`ui-card p-5 sm:p-6 min-w-0 flex-1 ${drawerOpen ? "lg:mr-[520px]" : ""}`}
    >
      <!-- Header -->
      <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start gap-3">
          <span
            class="inline-flex h-9 w-9 items-center justify-center border border-amber-200/50 bg-amber-400/60 text-amber-50"
          >
            <Icon icon={webhookIcon} width="18" height="18" />
          </span>

          <div>
            <h1 class="text-lg font-normal tracking-wide text-zinc-100">{$t('webhooks.title')}</h1>
            <p class="mt-0.5 text-sm text-zinc-400">
              {$t('webhooks.subtitle')}
            </p>
          </div>
        </div>

        <div class="flex flex-wrap gap-2">
          <div class={headerChip} title={$t('webhooks.base')}>
            <span class="text-zinc-400">{$t('webhooks.base')}</span>
            <span class="font-medium text-zinc-200">{apiBase || $t('webhooks.sameOrigin')}</span>
          </div>

          <div class={headerChip} title={$t('webhooks.jobs')}>
            <span class="text-zinc-400">{$t('webhooks.jobs')}</span>
            <span class="font-medium text-zinc-200">{totalJobs || 0}</span>
          </div>

          <button class={btn} on:click={() => fetchWebhooks()} disabled={loading} title={$t('common.refresh')}>
            <Icon icon={refreshIcon} width="18" height="18" />
          </button>
        </div>
      </div>

      <!-- Controls -->
      <div class="mt-6 grid gap-3 lg:grid-cols-[1fr_auto_auto_auto]">
        <div class="flex flex-col gap-2">
          <div class="text-xs text-zinc-500 flex items-center gap-2">
            <Icon icon={magnifyIcon} width="14" height="14" />
            <span>{$t('webhooks.searchHint')}</span>
          </div>
          <input class={inputCls} placeholder={$t('webhooks.searchPlaceholder')} bind:value={q} />
        </div>

        <div class="flex flex-col gap-2">
          <div class="text-xs text-zinc-500">{$t('webhooks.event')}</div>
          <select class={selectCls} bind:value={eventFilter}>
            <option value="all">{$t('common.all')}</option>
            <option value="picked">picked</option>
            <option value="starting">starting</option>
            <option value="finished">finished</option>
            <option value="error">{$t('common.error')}</option>
          </select>
        </div>

        <div class="flex flex-col gap-2">
          <div class="text-xs text-zinc-500">{$t('webhooks.pageSize')}</div>
          <select class={selectCls} bind:value={limit} on:change={() => fetchWebhooks(0)}>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </select>
        </div>

        <div class="flex flex-col gap-2">
          <div class="text-xs text-zinc-500">{$t('webhooks.cursor')}</div>
          <div class="flex items-center gap-2">
            <button class={btn} on:click={prev} disabled={loading || !cursorStack.length}>{$t('common.prev')}</button>
            <div class="text-xs text-zinc-400">
              <span class="text-zinc-200 font-medium">{cursor}</span>
              <span class="text-zinc-500"> &rarr; </span>
              <span class="text-zinc-200 font-medium">{nextCursor < 0 ? $t('common.end') : nextCursor}</span>
            </div>
            <button class={btn} on:click={next} disabled={loading || nextCursor < 0}>{$t('common.next')}</button>
          </div>
        </div>
      </div>

      {#if err}
        <div class="mt-4 rounded-xl border border-rose-400/35 bg-rose-400/10 px-4 py-3 text-sm text-rose-100">
          {err}
        </div>
      {/if}

      <!-- List -->
      <div class="mt-6">
        {#if loading}
          <div class="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400">
            {$t('common.loading')}
          </div>
        {:else if !groups?.length}
          <div class="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400">
            {$t('webhooks.noDeliveries')}
          </div>
        {:else}
          <div class="grid gap-3">
            {#each groups as g (g.key)}
              <button
                type="button"
                class={`${rowBase} ${selectedKey === g.key ? rowSelected : rowIdle}`}
                on:click={() => openGroup(g)}
              >
                <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <!-- Left -->
                  <div class="min-w-0 space-y-2">
                    <div class="flex items-center gap-2 flex-wrap">
                      <span class={`${pill} ${statusPillClass(g.status)}`}>
                        <Icon icon={statusIcon(g.status)} width="14" height="14" />
                        <span>{g.status}</span>
                      </span>

                      <span class="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-zinc-300">
                        {safeStr(g.event_type)}
                      </span>

                      <span class="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-zinc-400">
                        {$t('webhooks.deliveredCount', { delivered: g.delivered, total: g.total_targets })}
                      </span>

                      {#if g.failed}
                        <span class="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-zinc-300">
                          {$t('webhooks.failedCount', { count: g.failed })}
                        </span>
                      {/if}
                    </div>

                    <div class="font-mono text-sm text-zinc-100 truncate" title={safeStr(g.job_id)}>
                      {safeStr(g.job_id)}
                    </div>

                    <!-- per-target chips (neutral; only HTTP code colored) -->
                    <div class="flex flex-wrap gap-2">
                      {#each g.targets as tgt, j (tgt.url + "::" + j)}
                        <span
                          class="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px]"
                          title={safeStr(tgt.url)}
                        >
                          <span class="font-mono text-zinc-400 truncate max-w-[220px] sm:max-w-[280px]">
                            {shortUrl(tgt.url)}
                          </span>

                          <span class={`font-mono ${httpCodeClass(tgt.http_status)}`}>
                            HTTP {tgt.http_status ?? "\u2014"}
                          </span>

                          {#if tgt.duration_ms}
                            <span class="font-mono text-zinc-500">{fmtMs(tgt.duration_ms)}</span>
                          {/if}
                        </span>
                      {/each}
                    </div>
                  </div>

                  <!-- Right -->
                  <div class="shrink-0 text-right flex items-center gap-3">
                    <div class="text-right">
                      <div class="text-xs text-zinc-500">{$t('webhooks.time')}</div>
                      <div class="mt-1 font-mono text-xs text-zinc-200">
                        {fmtTime(g.last_ts)}
                      </div>
                    </div>
                    <Icon icon={chevronRightIcon} width="18" height="18" class="text-zinc-600" />
                  </div>
                </div>
              </button>
            {/each}
          </div>
        {/if}
      </div>
    </section>

    <!-- RIGHT DRAWER (not modal) -->
    {#if drawerOpen}
      <aside
        class="hidden lg:block fixed right-0 top-[88px] bottom-0 w-[520px]
               border-l border-white/10 bg-black/40 backdrop-blur-xl"
      >
        <div class="h-full overflow-auto p-6">
          <div class={`${card} p-5`}>
            <!-- Header -->
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0">
                <div class="text-xs font-medium uppercase tracking-wide text-zinc-400">
                  {$t('webhooks.webhookDelivery')}
                </div>
                <div
                  class="mt-1 text-xs text-zinc-500 truncate"
                  title={safeStr(selectedGroup?.job_id)}
                >
                  {safeStr(selectedGroup?.job_id)} &bull; {safeStr(selectedGroup?.event_type)}
                </div>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <a
                  class="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200"
                  href={`${apiBase}/webhooks${toQS({ cursor, limit })}`}
                  target="_blank"
                  rel="noreferrer"
                  title={$t('webhooks.openWebhooksJson')}
                >
                  <Icon icon={openInNewIcon} width="16" height="16" />
                  <span>{$t('common.json')}</span>
                </a>

                <button class={btn} on:click={closeDrawer} title={$t('common.close')}>
                  <Icon icon={closeIcon} width="18" height="18" />
                </button>
              </div>
            </div>

            <!-- Divider -->
            <div class="mt-4 border-t border-white/10 pt-4">
              {#if selectedGroup}
                <div class={`${card} p-4`}>
                  <div class="flex items-center justify-between">
                    <div class="text-xs font-medium uppercase tracking-wide text-zinc-400">
                      {$t('webhooks.summary')}
                    </div>

                    <span class={`${pill} ${statusPillClass(selectedGroup.status)}`}>
                      <Icon icon={statusIcon(selectedGroup.status)} width="14" height="14" />
                      <span>{selectedGroup.status}</span>
                    </span>
                  </div>

                  <div class="mt-4 grid gap-2 text-sm">
                    <div class="flex justify-between gap-3">
                      <div class="text-zinc-500">{$t('webhooks.delivered')}</div>
                      <div class="text-zinc-200">
                        {selectedGroup.delivered}/{selectedGroup.total_targets}
                      </div>
                    </div>
                    <div class="flex justify-between gap-3">
                      <div class="text-zinc-500">{$t('webhooks.failed')}</div>
                      <div class="text-zinc-200">{selectedGroup.failed}</div>
                    </div>
                    <div class="flex justify-between gap-3">
                      <div class="text-zinc-500">{$t('webhooks.time')}</div>
                      <div class="text-zinc-200">{fmtTime(selectedGroup.last_ts)}</div>
                    </div>
                  </div>

                  <div class="mt-5">
                    <div class="text-xs font-medium uppercase tracking-wide text-zinc-400">
                      {$t('webhooks.targets')}
                    </div>

                    <div class="mt-2 grid gap-2">
                      {#each selectedGroup.targets as tgt, j (tgt.url + "::" + j)}
                        {@const st = statusOfAttempt(tgt)}
                        <div class="rounded-xl border border-white/10 bg-black/20 p-3">
                          <div class="flex items-center justify-between gap-3">
                            <div class="min-w-0">
                              <div class="text-xs text-zinc-500">{$t('webhooks.url')}</div>
                              <div class="text-xs text-zinc-200 break-words">
                                {safeStr(tgt.url)}
                              </div>
                            </div>

                            <span class={`${pill} ${statusPillClass(st)}`}>
                              <Icon icon={statusIcon(st)} width="14" height="14" />
                              <span>{st}</span>
                            </span>
                          </div>

                          <div class="mt-3 flex flex-wrap gap-2">
                            <span class={tinyChip}>
                              <span class="text-zinc-400">HTTP</span>
                              <span class={`font-mono ${httpCodeClass(tgt.http_status)}`}>
                                {tgt.http_status ?? "\u2014"}
                              </span>
                            </span>

                            <span class={tinyChip}>
                              <span class="text-zinc-400">{$t('webhooks.dur')}</span>
                              <span class="font-mono text-zinc-200">{fmtMs(tgt.duration_ms)}</span>
                            </span>

                            {#if tgt.attempt}
                              <span class={tinyChip}>
                                <span class="text-zinc-400">{$t('webhooks.attempt')}</span>
                                <span class="font-mono text-zinc-200">{safeStr(tgt.attempt)}</span>
                              </span>
                            {/if}
                          </div>
                        </div>
                      {/each}
                    </div>
                  </div>
                </div>
              {:else}
                <div class="rounded-xl border border-white/10 bg-black/20 p-4 text-sm text-zinc-400">
                  {$t('webhooks.noSelection')}
                </div>
              {/if}
            </div>
          </div>
        </div>
      </aside>

      <!-- Mobile/Tablet fallback: inline drawer below list -->
      <div class="lg:hidden w-full mt-4">
        <div class="ui-card p-5">
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="text-sm font-semibold text-zinc-100">{$t('webhooks.webhookDelivery')}</div>
              <div
                class="mt-1 font-mono text-xs text-zinc-300 truncate"
                title={safeStr(selectedGroup?.job_id)}
              >
                {safeStr(selectedGroup?.job_id)} &bull; {safeStr(selectedGroup?.event_type)}
              </div>
            </div>

            <button class={btn} on:click={closeDrawer} title={$t('common.close')}>
              <Icon icon={closeIcon} width="18" height="18" />
            </button>
          </div>

          {#if selectedGroup}
            <div class="mt-4 rounded-xl border border-white/10 bg-black/20 p-4">
              <div class="text-xs text-zinc-500">{$t('webhooks.delivered')}</div>
              <div class="text-sm text-zinc-200">
                {selectedGroup.delivered}/{selectedGroup.total_targets}
              </div>

              <div class="mt-4 grid gap-2">
                {#each selectedGroup.targets as tgt, j (tgt.url + "::" + j)}
                  <div class="rounded-xl border border-white/10 bg-black/30 p-3">
                    <div class="text-xs text-zinc-500">{$t('webhooks.url')}</div>
                    <div class="text-xs text-zinc-200 break-words">{safeStr(tgt.url)}</div>

                    <div class="mt-2 flex flex-wrap gap-2">
                      <span class={tinyChip}>
                        <span class="text-zinc-400">HTTP</span>
                        <span class={`font-mono ${httpCodeClass(tgt.http_status)}`}>
                          {tgt.http_status ?? "\u2014"}
                        </span>
                      </span>
                      <span class={tinyChip}>
                        <span class="text-zinc-400">{$t('webhooks.dur')}</span>
                        <span class="font-mono text-zinc-200">{fmtMs(tgt.duration_ms)}</span>
                      </span>
                    </div>
                  </div>
                {/each}
              </div>
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</main>
